import { OverlayTrigger, Popover } from "react-bootstrap";
import { getProcessedTextPreview } from "../../../utilities/NewsUtilities";
import { useResponsiveTileTypography } from "../../CoTrader/Utilities/useResponsiveTileTypography";
import { formatScore } from "../../CoTrader/Utilities/updateDisplay";
import { FeedSkeleton } from "../../CoTrader/CoTraderSkeletons";
import { capitalize } from "../DashboardUtils";
import "../../CoTrader/CoTrader.css";
import "../ProductDashboard.css";

// Desktop Info Flow in the CoTrader desk language: articles render as
// tone-coded update cards (tone = article sentiment), with relevance and
// source in the footer. Mobile keeps NewsItem / NewsDetailDiv.

const isScore = (value) =>
  value !== null && value !== undefined && Number.isFinite(Number(value));

const getSentimentTone = (score) => {
  if (!isScore(score)) return { tone: "neutral", label: "News", icon: "fa-solid fa-newspaper" };
  const value = Number(score);
  if (value >= 0.33) return { tone: "positive", label: "Positive", icon: "fa-solid fa-arrow-trend-up" };
  if (value <= -0.33) return { tone: "negative", label: "Negative", icon: "fa-solid fa-arrow-trend-down" };
  return { tone: "neutral", label: "Neutral", icon: "fa-solid fa-minus" };
};

// Same bands as the relevance legend.
const getRelevanceLevel = (score) => {
  const value = Number(score);
  if (value > 0.5) return "high";
  if (value > 0) return "moderate";
  return "low";
};

const formatArticleDate = (timestamp) => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getSourceHost = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const getArticleTitle = (news) =>
  news.document_name || getProcessedTextPreview(news.processed_text, 120);

const RelevanceMeter = ({ score }) => {
  if (!isScore(score)) return null;
  const value = Number(score);
  const level = getRelevanceLevel(value);
  const width = Math.max(4, Math.min(1, Math.max(0, value)) * 100);

  return (
    <span
      className={`pd-relevance pd-relevance--${level}`}
      title={`${level[0].toUpperCase()}${level.slice(1)} relevance`}
    >
      Relevance
      <span className="pd-relevance-track" aria-hidden="true">
        <span className="pd-relevance-fill" style={{ width: `${width}%` }} />
      </span>
      {value.toFixed(2)}
    </span>
  );
};

const SourceLink = ({ url }) => {
  const host = getSourceHost(url);
  if (!url || !host) return null;

  return (
    <a
      className="pd-source"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      title={`Open source · ${host}`}
    >
      <span className="pd-source-host">{host}</span>
      <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
    </a>
  );
};

const ArticleKind = ({ news }) => {
  const sentiment = getSentimentTone(news.sentiment_score);
  return (
    <span className="ct-update-kind">
      <i className={sentiment.icon} aria-hidden="true" />
      {sentiment.label}
      {isScore(news.sentiment_score) && (
        <span className="pd-article-kind-score">
          {formatScore(Number(news.sentiment_score))}
        </span>
      )}
    </span>
  );
};

export const InfoFlowCard = ({ news, onOpen }) => {
  const { tone } = getSentimentTone(news.sentiment_score);
  const preview = news.summary || getProcessedTextPreview(news.processed_text, 220);

  return (
    <article
      className={`ct-update ct-update--${tone} pd-article`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="ct-update-head">
        <ArticleKind news={news} />
        <span className="ct-update-time" title={String(news.data_timestamp || "")}>
          {formatArticleDate(news.data_timestamp)}
        </span>
      </div>
      <h3 className="ct-update-title">{getArticleTitle(news)}</h3>
      {preview && <p className="ct-update-desc">{preview}</p>}
      {(isScore(news.ner_relevance_score) || news.public_data_source_url) && (
        <div className="ct-update-foot">
          <RelevanceMeter score={news.ner_relevance_score} />
          <SourceLink url={news.public_data_source_url} />
        </div>
      )}
    </article>
  );
};

export const InfoFlowDetail = ({ news, onClose }) => {
  const { tone } = getSentimentTone(news.sentiment_score);

  return (
    <div className="pd-detail">
      <button type="button" className="pd-detail-back" onClick={onClose}>
        <i className="fa-solid fa-arrow-left" aria-hidden="true" />
        All articles
      </button>
      <div className={`ct-update--${tone}`}>
        <ArticleKind news={news} />
      </div>
      <h3 className="pd-detail-title">{getArticleTitle(news)}</h3>
      <div className="pd-detail-meta">
        <span>{formatArticleDate(news.data_timestamp)}</span>
        <RelevanceMeter score={news.ner_relevance_score} />
        <SourceLink url={news.public_data_source_url} />
      </div>
      <div className="pd-detail-summary">
        <div className="ct-label">Summary</div>
        {news.summary || "No summary available for this item."}
      </div>
      <div className="pd-detail-hint">
        <i className="fa-regular fa-comment" aria-hidden="true" />
        Ask EMAlpha answers now use this article as context.
      </div>
    </div>
  );
};

const RelevanceLegend = (
  <Popover id="info-flow-relevance-popover">
    <Popover.Body>
      Articles are filtered by entity relevance and ordered newest first.
      <div className="mt-2">
        <strong>High</strong> above 0.50 · <strong>Moderate</strong> 0–0.50 ·{" "}
        <strong>Low</strong> 0 or below
      </div>
    </Popover.Body>
  </Popover>
);

const InfoFlowPanel = ({
  articles,
  isLoading,
  isMoreLoading,
  selectedNews,
  onOpen,
  onClose,
  selectedPoint,
  selectedDate,
  onClearFocus,
  scrollRef,
  isFocused = false,
}) => {
  const { containerRef, tier } = useResponsiveTileTypography();
  const hasRelevance = articles.some((article) => isScore(article.ner_relevance_score));
  const focusLabel = selectedPoint
    ? [
        selectedPoint.name,
        capitalize(selectedPoint.country),
        selectedDate instanceof Date ? selectedDate.toLocaleDateString("en-US") : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <section
      ref={containerRef}
      data-tier={tier}
      className={`ct-panel ct-insights pd-flow ${isFocused ? "is-focused" : ""}`}
    >
      <div className="ct-tabs pd-flow-head">
        <h2 className="ct-tab-title">Info Flow</h2>
        <div className="pd-flow-tools">
          {focusLabel && (
            <span className="pd-focus-chip" title={focusLabel}>
              <i className="fa-solid fa-crosshairs" aria-hidden="true" />
              <span>{focusLabel}</span>
              <button
                type="button"
                onClick={onClearFocus}
                title="Show the full date range"
                aria-label="Clear date focus"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </span>
          )}
          {!isLoading && articles.length > 0 && (
            <span className="pd-flow-count">
              {articles.length} {articles.length === 1 ? "article" : "articles"}
            </span>
          )}
          {hasRelevance && (
            <OverlayTrigger
              placement="bottom-end"
              trigger={["hover", "focus"]}
              overlay={RelevanceLegend}
            >
              <i
                className="fa-solid fa-circle-info pd-flow-info"
                tabIndex={0}
                aria-label="About relevance"
              />
            </OverlayTrigger>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="ct-feed">
        {isLoading ? (
          <FeedSkeleton count={4} />
        ) : selectedNews ? (
          <InfoFlowDetail news={selectedNews} onClose={onClose} />
        ) : articles.length > 0 ? (
          <>
            {articles.map((news, index) => (
              <InfoFlowCard
                key={`${news.news_url || news.public_data_source_url}-${index}`}
                news={news}
                onOpen={() => onOpen(news)}
              />
            ))}
            {isMoreLoading && <FeedSkeleton count={1} />}
          </>
        ) : (
          <div className="ct-feed-empty">
            <i className="fa-regular fa-newspaper" aria-hidden="true" />
            No relevant articles for this selection.
          </div>
        )}
      </div>
    </section>
  );
};

export default InfoFlowPanel;
